/**
 * The session API (KIT_REQUIREMENTS.md §2, wp/shell §5.1–§5.3): starting and ending a session, switching workspace,
 * software and account, refreshing the session and the account data the chrome shows. The only code that turns API
 * answers into the stored session (`src/app/stores/auth.ts`). Modules use it instead of calling `main/switchCompany`
 * or `main/switchSoftware` themselves (WP-06, WP-17).
 */
import type { QueryClient } from '@tanstack/vue-query'
import type { Router } from 'vue-router'

import { currentLocale, setLocale } from '@/app/i18n'
import { DEFAULT_DATE_FORMAT } from '@/shared/utils/format'

import { getHomeRoute } from '../router/home'
import { ROUTE_LOGIN } from '../router/names'
import { useAuthStore } from '../stores/auth'
import { useCountsStore } from '../stores/counts'
import {
  EMPLOYER_SOFTWARES,
  isSecondFactorStep,
  isSoftwareCode,
  sessionFromLoginResponse,
  SOFTWARE_IDS,
  type LoginSessionData,
  type Session,
  type SoftwareCode,
} from '../stores/session'

import {
  getAccountProfile,
  getTimeformats,
  getWorkspaces,
  postSwitchCompany,
  postSwitchSoftware,
  postLogout,
  postToggleAccount,
  refreshLogin,
} from './api'

export type { OtherAccount } from './api'

interface Dependencies {
  router: Router
  queryClient?: QueryClient
}

let deps: Dependencies | null = null

/** Boot (src/app/main.ts): gives the session API the router and the query cache it clears on logout. */
export function installSession(dependencies: Dependencies): void {
  deps = dependencies
}

function router(): Router {
  if (!deps) throw new Error('installSession() has not run')
  return deps.router
}

async function dateFormatFor(key: string | null | undefined): Promise<string> {
  try {
    const formats = (await getTimeformats()).data
    return (key && formats[key]) || DEFAULT_DATE_FORMAT
  } catch {
    // NEW-16: a failing timeformats call no longer blocks the sign-in.
    return DEFAULT_DATE_FORMAT
  }
}

/**
 * Stores the session of a login answer, then the account's date format (`settings/timeformats` of
 * `menu.user.Timeformat`, `DD/MM/YYYY` when the call fails — NEW-16).
 */
export async function startSession(data: LoginSessionData): Promise<Session> {
  const auth = useAuthStore()
  auth.setSession(sessionFromLoginResponse(data))
  auth.update({ dateFormat: await dateFormatFor(auth.session?.dateFormatKey) })
  return auth.session as Session
}

/**
 * Ends the session locally: stops the badge polling, forgets the counts and the cached queries, deletes the session
 * from storage (preferences stay: locale, columns, "don't remind" — NEW-1). Local only: the 401 handler (the token is
 * already dead), the account switch and the set-password page use it as is; `logout()` also expires the token.
 */
export function endSession(): void {
  useCountsStore().reset()
  useAuthStore().clearSession()
  deps?.queryClient?.clear()
}

/**
 * The one user logout (user menu, not-authorized page — wp/shell §4.17): expires the token on the API first
 * (`POST users/logout`, D-43 — with "Remember me" a token lives 7 days, BR-080), then ends the session and goes to
 * `/login`. The API call is best effort (5 s): a failure never keeps the user signed in. The API keeps one token per
 * account and IP address, so the account's other tabs and apps on the same network are logged out with it (OPEN-11 (b)).
 */
export async function logout(): Promise<void> {
  if (useAuthStore().session) await postLogout().catch(() => undefined)
  endSession()
  await router().push({ name: ROUTE_LOGIN })
}

/**
 * Re-reads the session with `POST users/publicLogin` (no body, live token): the Staff permission matrix, the softwares
 * and workspaces. The nav is computed from the store, so it follows (NEW-9). A dead token answers 401 → logout.
 */
export async function refreshSession(): Promise<void> {
  const auth = useAuthStore()
  if (!auth.session) return
  const { data } = await refreshLogin()
  if (isSecondFactorStep(data) || !auth.session) return
  const fresh = sessionFromLoginResponse(data)
  auth.update({
    permissions: fresh.permissions,
    softwares: fresh.softwares,
    workspaces: fresh.workspaces.length ? fresh.workspaces : auth.session.workspaces,
    profile: { ...auth.session.profile, ...fresh.profile },
  })
}

/** `GET system/workspaces` → the stored workspace list (the navbar select). */
export async function loadWorkspaces(): Promise<void> {
  const auth = useAuthStore()
  const { data } = await getWorkspaces()
  if (auth.session) auth.update({ workspaces: data.workspaces ?? [] })
}

/**
 * Applies a language chosen by the account (profile, Language & date tab): loads its catalogues, sets the UI locale
 * and keeps it in the preferences (it survives logout, as legacy's `locale`).
 */
export async function applyLanguage(
  locale: string | null | undefined,
  catalogues?: string | readonly string[],
): Promise<void> {
  if (!locale) return
  useAuthStore().setLocale(locale)
  if (locale !== currentLocale()) await setLocale(locale, catalogues)
}

/**
 * What the navbar loads when the vertical layout mounts (legacy `AppNavbarVerticalLayout.vue:419-463`): the account
 * (its language becomes the UI language) and the date format. Legacy read `users/profile` a second time for the date
 * format; one read serves both.
 */
export async function loadAccount(catalogues?: string | readonly string[]): Promise<void> {
  const auth = useAuthStore()
  if (!auth.session) return
  const [profile, dateFormat] = await Promise.all([
    getAccountProfile(auth.session.userType).then((response) => response.data),
    getTimeformats()
      .then((response) => response.data)
      .catch(() => null),
  ])
  if (!auth.session) return
  const key = profile.Timeformat ?? auth.session.dateFormatKey
  auth.update({
    profile: { ...auth.session.profile, ...profile },
    dateFormatKey: key ?? null,
    dateFormat: (key && dateFormat?.[key]) || auth.session.dateFormat || DEFAULT_DATE_FORMAT,
  })
  await applyLanguage(profile.Language, catalogues)
}

/**
 * Workspace switch (navbar select, wp/shell §4.16): `main/switchCompany`, store the workspace, then reload the same URL
 * (OPEN-4 default (a)). The softwares depend on the workspace — a Freelancer's `em` only with an application there
 * (candidate-app OPEN-9), an employer's Projects (`pm`) only where the workspace has it (docs/projects, PM-8) — so the
 * session is refreshed first.
 */
export async function switchCompany(companyId: number, options: { reload?: boolean } = {}): Promise<void> {
  const auth = useAuthStore()
  await postSwitchCompany(companyId)
  const workspace = auth.session?.workspaces.find((entry) => entry.Id === companyId) ?? null
  auth.setCurrentWorkspace(workspace)
  await refreshSession().catch(() => undefined)
  if (options.reload !== false) router().go(0)
}

/**
 * Software switch (nav button group, wp/shell §4.18): `main/switchSoftware`, store it, go to the new software's home
 * (`getHomeRoute()`), re-read the badges (their keys depend on the server's software).
 */
export async function switchSoftware(code: SoftwareCode): Promise<void> {
  const auth = useAuthStore()
  const software = auth.session?.softwares.find((entry) => entry.short === code)
  await postSwitchSoftware(software?.id ?? SOFTWARE_IDS[code])
  auth.setCurrentSoftware(code)
  await router().replace(getHomeRoute(auth.session))
  void useCountsStore().refresh()
}

/**
 * Account switch (navbar "Switch current role", wp/shell §4.16b): `users/toggleAccount` with the password, store the
 * other account's session, then a full reload on `/` (legacy `window.location.href = '/'`). Rejects with the API error
 * (wrong password, 2FA account — FX-044) so the modal stays open.
 */
export async function switchAccount(userId: number, password: string): Promise<string | null> {
  const response = await postToggleAccount(userId, password)
  useCountsStore().reset()
  await startSession(response.data)
  deps?.queryClient?.clear()
  await router().replace('/')
  router().go(0)
  return response.message
}

/**
 * OPEN-5 default (a): a navigation into another employer software stores it on the server too, so the badges and the
 * server's defaults follow the visible software (legacy meant to, `App.vue:113-134`, NEW-3).
 */
export function syncSoftwareWithRoute(to: { path: string }): void {
  const auth = useAuthStore()
  const session = auth.session
  const segment = to.path.split('/')[1] ?? ''
  if (!session || !isSoftwareCode(segment) || !(EMPLOYER_SOFTWARES as readonly string[]).includes(segment)) return
  if (segment === session.currentSoftware) return
  const software = session.softwares.find((entry) => entry.short === segment)
  if (!software) return
  auth.setCurrentSoftware(segment)
  postSwitchSoftware(software.id)
    .then(() => useCountsStore().refresh())
    .catch(() => undefined)
}
